import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { extractTenantSlug } from '@/lib/tenant';

// Storage key for remembering the last organization ID
const LAST_ORG_KEY = 'blazepos-last-org-id';

/**
 * Hook to fetch organization-level login setting before authentication.
 * Uses tenant slug from subdomain, or falls back to stored org ID or first available org.
 */
export function useOrgLoginSetting() {
  const [useAlternativeLogin, setUseAlternativeLogin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchSetting() {
      try {
        const slug = extractTenantSlug();
        
        let orgSettings: { use_alternative_login?: boolean } | null = null;
        let foundOrg = false;

        if (slug) {
          // Multi-tenant: Fetch organization settings by slug
          const { data, error } = await supabase
            .from('organizations')
            .select('id, slug, settings')
            .eq('slug', slug)
            .single();

          if (!error && data) {
            orgSettings = data.settings as { use_alternative_login?: boolean } | null;
            foundOrg = true;
            // Store org ID for future use
            localStorage.setItem(LAST_ORG_KEY, data.id);
          }
        }
        
        // Fallback: Try stored organization ID (from last login)
        if (!foundOrg) {
          const lastOrgId = localStorage.getItem(LAST_ORG_KEY);
          if (lastOrgId) {
            const { data, error } = await supabase
              .from('organizations')
              .select('id, slug, settings')
              .eq('id', lastOrgId)
              .single();

            if (!error && data) {
              orgSettings = data.settings as { use_alternative_login?: boolean } | null;
              foundOrg = true;
            }
          }
        }

        // Final fallback: Fetch first organization (for dev/single-tenant setups)
        if (!foundOrg) {
          const { data, error } = await supabase
            .from('organizations')
            .select('id, slug, settings')
            .limit(1)
            .single();

          if (!error && data) {
            orgSettings = data.settings as { use_alternative_login?: boolean } | null;
            localStorage.setItem(LAST_ORG_KEY, data.id);
          }
        }

        const finalValue = orgSettings?.use_alternative_login ?? false;
        setUseAlternativeLogin(finalValue);
      } catch (err) {
        console.error('[useOrgLoginSetting] Error fetching login setting');
        setUseAlternativeLogin(false);
      } finally {
        setIsLoading(false);
      }
    }

    fetchSetting();
  }, []);

  return { useAlternativeLogin, isLoading };
}

